import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ExternalLink,
  RefreshCw,
  Play,
  Trash2,
  Activity,
  Settings,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Wifi,
  Save,
  BarChart3,
  Send,
  Mail,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Search,
  X,
} from 'lucide-react';
import type { QuarterlyStats, ReportRun } from '../types';
import { api } from '../services/api';
import StatusBadge from '../components/StatusBadge';
import HealthCard from '../components/HealthCard';
import BackupTable from '../components/BackupTable';
import ScreenshotGallery from '../components/ScreenshotGallery';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';

interface KumaMonitor {
  id: number;
  name: string;
  url: string;
  type: string;
  active: boolean;
}

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'backups' | 'screenshots' | 'berichte'>('backups');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedMonitorIds, setSelectedMonitorIds] = useState<string[]>([]);
  const [monitorsDirty, setMonitorsDirty] = useState(false);
  const [monitorPickerOpen, setMonitorPickerOpen] = useState(false);
  const [monitorSearch, setMonitorSearch] = useState('');
  const [monitorSortSnapshot, setMonitorSortSnapshot] = useState<string[]>([]);
  const [connectionResult, setConnectionResult] = useState<{ success: boolean; message: string } | null>(null);

  const { data: project, isLoading } = useQuery({
    queryKey: ['project', id],
    queryFn: () => api.projects.get(id!),
    enabled: !!id,
  });

  const { data: kumaMonitors = [], isLoading: kumaLoading, isError: kumaError } = useQuery<KumaMonitor[]>({
    queryKey: ['kumaMonitors'],
    queryFn: () => api.settings.getKumaMonitors(),
    retry: 1,
  });

  const { data: allMonitorStatus } = useQuery({
    queryKey: ['allKumaStatus'],
    queryFn: () => api.settings.getAllKumaStatus(),
    enabled: selectedMonitorIds.length > 0,
    refetchInterval: 30000,
  });

  useEffect(() => {
    if (project?.kumaMonitorIds) {
      setSelectedMonitorIds(project.kumaMonitorIds);
      setMonitorsDirty(false);
    }
  }, [project?.kumaMonitorIds]);

  const healthCheckMutation = useMutation({
    mutationFn: () => api.projects.healthCheck(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', id] });
    },
  });

  const triggerBackupMutation = useMutation({
    mutationFn: () => api.backups.trigger(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      queryClient.invalidateQueries({ queryKey: ['backups', id] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.projects.delete(id!),
    onSuccess: () => {
      navigate('/projects');
    },
  });

  const updateKumaMonitorsMutation = useMutation({
    mutationFn: (monitorIds: string[]) =>
      api.projects.update(id!, { kumaMonitorIds: monitorIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      setMonitorsDirty(false);
    },
  });

  const testConnectionMutation = useMutation({
    mutationFn: () => api.projects.testConnection(id!),
    onSuccess: (data) => {
      setConnectionResult(data);
      setTimeout(() => setConnectionResult(null), 5000);
    },
    onError: (err: Error) => {
      setConnectionResult({ success: false, message: err.message });
      setTimeout(() => setConnectionResult(null), 5000);
    },
  });

  const handleMonitorToggle = (monitorId: string) => {
    setSelectedMonitorIds((prev) => {
      const next = prev.includes(monitorId)
        ? prev.filter((id) => id !== monitorId)
        : [...prev, monitorId];
      return next;
    });
    setMonitorsDirty(true);
  };

  const handleSaveMonitors = () => {
    updateKumaMonitorsMutation.mutate(selectedMonitorIds);
  };

  const handleToggleMonitorPicker = () => {
    setMonitorPickerOpen((prev) => {
      if (!prev) {
        // Sortier-Reihenfolge beim Öffnen einfrieren, damit Einträge beim
        // An- und Abwählen nicht unter dem Cursor wegspringen
        setMonitorSortSnapshot(selectedMonitorIds);
        setMonitorSearch('');
      }
      return !prev;
    });
  };

  const visibleMonitors = useMemo(() => {
    const query = monitorSearch.trim().toLowerCase();
    const filtered = query
      ? kumaMonitors.filter(
          (m) =>
            m.name.toLowerCase().includes(query) ||
            (m.url || '').toLowerCase().includes(query)
        )
      : kumaMonitors;
    const preSelected = new Set(monitorSortSnapshot);
    return [...filtered].sort((a, b) => {
      const aSel = preSelected.has(String(a.id)) ? 0 : 1;
      const bSel = preSelected.has(String(b.id)) ? 0 : 1;
      if (aSel !== bSel) return aSel - bSel;
      return a.name.localeCompare(b.name, 'de');
    });
  }, [kumaMonitors, monitorSearch, monitorSortSnapshot]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Projekt nicht gefunden</p>
        <Link to="/projects" className="text-primary-600 hover:underline mt-2 inline-block">
          Zurück zur Liste
        </Link>
      </div>
    );
  }

  const tabs = [
    { key: 'backups', label: 'Backups' },
    { key: 'screenshots', label: 'Screenshots' },
    { key: 'berichte', label: 'Berichte' },
  ] as const;

  return (
    <div>
      <Link
        to="/projects"
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Zurück zu Projekten
      </Link>

      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
            <StatusBadge status={project.status} />
          </div>
          <a
            href={project.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-600 hover:text-primary-600 flex items-center gap-1"
          >
            {project.url}
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowSettingsModal(true)}
            className="btn btn-secondary flex items-center gap-2"
          >
            <Settings className="w-4 h-4" />
            Einstellungen
          </button>
          <button
            onClick={() => triggerBackupMutation.mutate()}
            disabled={triggerBackupMutation.isPending}
            className="btn btn-primary flex items-center gap-2"
          >
            <Play className="w-4 h-4" />
            {triggerBackupMutation.isPending ? 'Wird gestartet...' : 'Backup starten'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="card p-4">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Letztes Backup</h3>
          <p className="text-lg font-semibold">
            {project.lastBackupAt
              ? formatDistanceToNow(new Date(project.lastBackupAt), {
                  addSuffix: true,
                  locale: de,
                })
              : 'Noch kein Backup'}
          </p>
        </div>
        <div className="card p-4">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Health Check</h3>
          <div className="flex items-center justify-between">
            <p className="text-lg font-semibold">
              {project.lastHealthCheckAt
                ? formatDistanceToNow(new Date(project.lastHealthCheckAt), {
                    addSuffix: true,
                    locale: de,
                  })
                : 'Nie'}
            </p>
            <button
              onClick={() => healthCheckMutation.mutate()}
              disabled={healthCheckMutation.isPending}
              className="text-primary-600 hover:text-primary-700"
              title="Health Check durchführen"
            >
              <RefreshCw
                className={`w-4 h-4 ${healthCheckMutation.isPending ? 'animate-spin' : ''}`}
              />
            </button>
          </div>
        </div>
        <div className="card p-4">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Validator</h3>
          <p className="text-lg font-semibold">
            {project.lastValidatorAt
              ? formatDistanceToNow(new Date(project.lastValidatorAt), {
                  addSuffix: true,
                  locale: de,
                })
              : 'Nie getestet'}
          </p>
        </div>
        <div className="card p-4">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Verbindung</h3>
          <div className="flex items-center justify-between">
            {connectionResult ? (
              <div className="flex items-center gap-2">
                {connectionResult.success ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-500" />
                )}
                <span className={`text-sm font-medium ${connectionResult.success ? 'text-green-600' : 'text-red-600'}`}>
                  {connectionResult.success ? 'Verbunden' : 'Fehler'}
                </span>
              </div>
            ) : (
              <p className="text-sm text-gray-400">Nicht getestet</p>
            )}
            <button
              onClick={() => testConnectionMutation.mutate()}
              disabled={testConnectionMutation.isPending}
              className="text-primary-600 hover:text-primary-700"
              title="Verbindung testen"
            >
              {testConnectionMutation.isPending ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Wifi className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>

      {project.latestHealthCheck && (
        <div className="mb-6">
          <HealthCard healthCheck={project.latestHealthCheck} />
        </div>
      )}

      <div className="card mb-6">
        <div className="border-b">
          <div className="flex gap-4 px-4">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-3 px-2 border-b-2 font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        <div className="p-4">
          {activeTab === 'backups' && <BackupTable projectId={id!} />}
          {activeTab === 'screenshots' && (
            <ScreenshotGallery
              screenshots={project.latestValidatorRun?.screenshots || []}
            />
          )}
          {activeTab === 'berichte' && <ProjectReportTab projectId={id!} project={project} />}
        </div>
      </div>

      {/* Kuma Monitor Card */}
      <div className="card p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary-600" />
            <h3 className="font-medium">Uptime Kuma Monitors</h3>
            {selectedMonitorIds.length > 0 && (
              <span className="bg-primary-100 text-primary-700 text-xs font-medium px-2 py-0.5 rounded-full">
                {selectedMonitorIds.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {monitorsDirty && (
              <button
                onClick={handleSaveMonitors}
                disabled={updateKumaMonitorsMutation.isPending}
                className="btn btn-primary btn-sm flex items-center gap-1.5 text-sm px-3 py-1.5"
              >
                {updateKumaMonitorsMutation.isPending ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Save className="w-3.5 h-3.5" />
                )}
                Speichern
              </button>
            )}
            {!monitorsDirty && updateKumaMonitorsMutation.isSuccess && (
              <span className="text-green-600 text-sm flex items-center gap-1">
                <CheckCircle className="w-3.5 h-3.5" />
                Gespeichert
              </span>
            )}
            {!kumaLoading && !kumaError && kumaMonitors.length > 0 && (
              <button
                onClick={handleToggleMonitorPicker}
                aria-expanded={monitorPickerOpen}
                className="btn btn-secondary btn-sm flex items-center gap-1.5 text-sm px-3 py-1.5"
              >
                {monitorPickerOpen ? (
                  <ChevronUp className="w-3.5 h-3.5" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5" />
                )}
                {monitorPickerOpen ? 'Fertig' : 'Auswahl bearbeiten'}
              </button>
            )}
          </div>
        </div>
        {kumaLoading ? (
          <div className="flex items-center gap-2 text-gray-500">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span>Lade Monitors...</span>
          </div>
        ) : kumaError ? (
          <p className="text-red-500 text-sm">
            Fehler beim Laden der Monitors. Bitte Uptime Kuma Verbindung in den{' '}
            <Link to="/settings" className="text-red-600 hover:underline">
              Einstellungen
            </Link>{' '}
            prüfen.
          </p>
        ) : kumaMonitors.length > 0 ? (
          <div className="space-y-4">
            {monitorPickerOpen && (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    value={monitorSearch}
                    onChange={(e) => setMonitorSearch(e.target.value)}
                    placeholder="Monitor suchen (Name oder URL)..."
                    className="input pl-9 pr-9 text-sm"
                  />
                  {monitorSearch && (
                    <button
                      type="button"
                      onClick={() => setMonitorSearch('')}
                      aria-label="Suche zurücksetzen"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  {visibleMonitors.length} von {kumaMonitors.length} Monitoren
                  {selectedMonitorIds.length > 0 && ` · ${selectedMonitorIds.length} ausgewählt`}
                </p>
                {visibleMonitors.length > 0 ? (
                  <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                    {visibleMonitors.map((monitor) => {
                      const monitorIdStr = String(monitor.id);
                      const isSelected = selectedMonitorIds.includes(monitorIdStr);
                      const status = allMonitorStatus?.[monitorIdStr];
                      return (
                        <label
                          key={monitor.id}
                          className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                            isSelected
                              ? 'border-primary-300 bg-primary-50'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleMonitorToggle(monitorIdStr)}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-sm">{monitor.name}</span>
                            <span className="text-xs text-gray-400 ml-2">({monitor.type})</span>
                          </div>
                          {isSelected && status && (
                            <div className="flex items-center gap-3 text-xs text-gray-600">
                              <span
                                className={`w-2 h-2 rounded-full ${
                                  status.status === 'up'
                                    ? 'bg-green-500'
                                    : status.status === 'down'
                                    ? 'bg-red-500'
                                    : status.status === 'maintenance'
                                    ? 'bg-yellow-500'
                                    : 'bg-gray-400'
                                }`}
                              />
                              <span>{status.uptime24h}%</span>
                              <span>{status.latency}ms</span>
                            </div>
                          )}
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 py-6 text-center">
                    Kein Monitor gefunden für „{monitorSearch}“.
                  </p>
                )}
              </div>
            )}
            {selectedMonitorIds.length > 0 && allMonitorStatus && (
              <div className={monitorPickerOpen ? 'pt-3 border-t' : ''}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {selectedMonitorIds.map((monitorId) => {
                    const status = allMonitorStatus[monitorId];
                    if (!status) return null;
                    return (
                      <div key={monitorId} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                        <span
                          className={`w-3 h-3 rounded-full flex-shrink-0 ${
                            status.status === 'up'
                              ? 'bg-green-500'
                              : status.status === 'down'
                              ? 'bg-red-500'
                              : status.status === 'maintenance'
                              ? 'bg-yellow-500'
                              : 'bg-gray-400'
                          }`}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{status.name}</p>
                          <p className="text-xs text-gray-500">
                            {status.status === 'up' ? 'Online' : status.status === 'down' ? 'Offline' : status.status === 'maintenance' ? 'Wartung' : 'Unbekannt'}
                            {' · '}{status.uptime24h}% · {status.latency}ms
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {selectedMonitorIds.length > 0 && !allMonitorStatus && (
              <div className={monitorPickerOpen ? 'pt-3 border-t' : ''}>
                <p className="text-sm text-gray-500 flex items-center gap-2">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  Status wird geladen...
                </p>
              </div>
            )}
            {!monitorPickerOpen && selectedMonitorIds.length === 0 && (
              <p className="text-gray-500 text-sm">
                Noch kein Monitor zugewiesen — über „Auswahl bearbeiten“ zuordnen.
              </p>
            )}
          </div>
        ) : (
          <p className="text-gray-500 text-sm">
            Keine Monitors verfügbar. Bitte Uptime Kuma in den{' '}
            <Link to="/settings" className="text-primary-600 hover:underline">
              Einstellungen
            </Link>{' '}
            konfigurieren und Verbindung testen.
          </p>
        )}
      </div>

      {/* Settings Modal */}
      {showSettingsModal && (
        <ProjectSettingsModal
          project={project}
          onClose={() => setShowSettingsModal(false)}
          onDelete={() => {
            setShowSettingsModal(false);
            setShowDeleteModal(true);
          }}
        />
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">Projekt löschen?</h2>
            <p className="text-gray-600 mb-6">
              Sind Sie sicher, dass Sie "{project.name}" löschen möchten? Diese
              Aktion kann nicht rückgängig gemacht werden.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="btn btn-secondary"
              >
                Abbrechen
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="btn btn-danger"
              >
                {deleteMutation.isPending ? 'Löschen...' : 'Löschen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Project Settings Modal
function ProjectSettingsModal({
  project,
  onClose,
  onDelete,
}: {
  project: { id: string; name: string; url: string };
  onClose: () => void;
  onDelete: () => void;
}) {
  const [name, setName] = useState(project.name);
  const [url, setUrl] = useState(project.url);
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: (data: { name?: string; url?: string; apiKey?: string }) =>
      api.projects.update(project.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', project.id] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setSuccess(true);
      setApiKey('');
      setTimeout(() => setSuccess(false), 2000);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const testConnectionMutation = useMutation({
    mutationFn: () => api.projects.testConnection(project.id),
    onSuccess: (data) => {
      if (data.success) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 2000);
      } else {
        setError(data.message);
      }
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const updates: { name?: string; url?: string; apiKey?: string } = {};

    if (name !== project.name) updates.name = name;
    if (url !== project.url) updates.url = url;
    if (apiKey) updates.apiKey = apiKey;

    if (Object.keys(updates).length === 0) {
      setError('Keine Änderungen vorgenommen');
      return;
    }

    updateMutation.mutate(updates);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
        <h2 className="text-xl font-bold mb-6">Projekt-Einstellungen</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              required
            />
          </div>

          <div>
            <label className="label">URL</label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="input"
              required
            />
          </div>

          <div>
            <label className="label">
              API Key{' '}
              <span className="text-gray-400 font-normal">
                (leer lassen um beizubehalten)
              </span>
            </label>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="input pr-10"
                placeholder="Neuer API Key"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showApiKey ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">{error}</p>
          )}

          {success && (
            <p className="text-sm text-green-600 bg-green-50 p-3 rounded-lg flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Erfolgreich gespeichert
            </p>
          )}

          <div className="flex items-center justify-between pt-4 border-t">
            <button
              type="button"
              onClick={() => testConnectionMutation.mutate()}
              disabled={testConnectionMutation.isPending}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {testConnectionMutation.isPending
                ? 'Teste Verbindung...'
                : 'Verbindung testen'}
            </button>

            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="btn btn-secondary">
                Schließen
              </button>
              <button
                type="submit"
                disabled={updateMutation.isPending}
                className="btn btn-primary"
              >
                {updateMutation.isPending ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </div>
        </form>

        <div className="mt-6 pt-6 border-t">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Gefahrenzone</h3>
          <button
            onClick={onDelete}
            className="btn btn-danger flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Projekt löschen
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Berichte-Tab ─────────────────────────────────────────────────────────────

function getPreviousQuarter(): { year: number; quarter: number } {
  const now = new Date();
  const currentQ = Math.floor(now.getMonth() / 3) + 1;
  if (currentQ === 1) return { year: now.getFullYear() - 1, quarter: 4 };
  return { year: now.getFullYear(), quarter: currentQ - 1 };
}

function formatBytesDetail(bytes: string): string {
  const n = parseInt(bytes, 10);
  if (isNaN(n) || n === 0) return '0 Bytes';
  if (n >= 1024 * 1024 * 1024) return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${n} Bytes`;
}

function ProjectReportTab({
  projectId,
  project,
}: {
  projectId: string;
  project: { name: string; customerEmail: string | null };
}) {
  const prev = getPreviousQuarter();
  const [selectedYear, setSelectedYear] = useState(prev.year);
  const [selectedQuarter, setSelectedQuarter] = useState(prev.quarter);
  const [emailInput, setEmailInput] = useState(project.customerEmail ?? '');
  const [emailSaved, setEmailSaved] = useState(false);
  const [sendResult, setSendResult] = useState<{ success: boolean; message: string } | null>(null);

  const queryClient = useQueryClient();
  const currentYear = new Date().getFullYear();

  const { data: stats, isLoading: statsLoading } = useQuery<QuarterlyStats>({
    queryKey: ['report-preview', projectId, selectedYear, selectedQuarter],
    queryFn: () => api.reports.preview(projectId, selectedYear, selectedQuarter),
  });

  const { data: history } = useQuery<ReportRun[]>({
    queryKey: ['report-history', projectId],
    queryFn: () => api.reports.history(projectId),
  });

  const saveEmailMutation = useMutation({
    mutationFn: (email: string | null) =>
      api.projects.update(projectId, { customerEmail: email }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setEmailSaved(true);
      setTimeout(() => setEmailSaved(false), 2000);
    },
  });

  const sendMutation = useMutation({
    mutationFn: () => api.reports.send(projectId, selectedYear, selectedQuarter),
    onSuccess: (data) => {
      setSendResult({ success: data.success, message: data.message });
      queryClient.invalidateQueries({ queryKey: ['report-history', projectId] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      setTimeout(() => setSendResult(null), 5000);
    },
    onError: (err: Error) => {
      setSendResult({ success: false, message: err.message });
      setTimeout(() => setSendResult(null), 5000);
    },
  });

  const currentEmail = project.customerEmail;
  const quarterLabel = `Q${selectedQuarter} ${selectedYear}`;

  return (
    <div className="space-y-6 py-2">
      {/* Kunden-E-Mail */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Mail className="w-4 h-4" />
          Kunden-E-Mail für Berichte
        </h3>
        <div className="flex gap-2 items-center">
          <input
            type="email"
            value={emailInput}
            onChange={e => setEmailInput(e.target.value)}
            placeholder="kunde@example.com"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <button
            onClick={() => saveEmailMutation.mutate(emailInput || null)}
            disabled={saveEmailMutation.isPending || emailInput === (currentEmail ?? '')}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {saveEmailMutation.isPending ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : emailSaved ? (
              <CheckCircle className="w-4 h-4" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {emailSaved ? 'Gespeichert' : 'Speichern'}
          </button>
        </div>
        {!currentEmail && (
          <p className="text-xs text-amber-600 mt-1.5">
            Ohne Kunden-E-Mail kann kein Bericht versendet werden.
          </p>
        )}
      </div>

      {/* Quartal-Selector */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <BarChart3 className="w-4 h-4" />
          Quartal auswählen
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => {
              if (selectedQuarter === 1) { setSelectedYear(y => y - 1); setSelectedQuarter(4); }
              else setSelectedQuarter(q => q - 1);
            }}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          {[1, 2, 3, 4].map(q => (
            <button
              key={q}
              onClick={() => setSelectedQuarter(q)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                selectedQuarter === q
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Q{q}
            </button>
          ))}
          <button
            onClick={() => {
              if (selectedQuarter === 4) { setSelectedYear(y => y + 1); setSelectedQuarter(1); }
              else setSelectedQuarter(q => q + 1);
            }}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <select
            value={selectedYear}
            onChange={e => setSelectedYear(parseInt(e.target.value, 10))}
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {Array.from({ length: 4 }, (_, i) => currentYear - i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Vorschau-Karten */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Vorschau {quarterLabel}</h3>
        {statsLoading ? (
          <div className="flex items-center gap-2 py-4 text-gray-400">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span className="text-sm">Daten werden geladen…</span>
          </div>
        ) : stats ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Backups</p>
                <p className="text-xl font-bold text-gray-900">{stats.backupsCompleted}</p>
                <p className="text-xs text-green-700">
                  {stats.backupsFailed > 0 ? `${stats.backupsFailed} fehlgeschlagen` : 'alle erfolgreich'}
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Volumen</p>
                <p className="text-xl font-bold text-gray-900">{formatBytesDetail(stats.totalStorageBytes)}</p>
                <p className="text-xs text-blue-700">gesichertes Datenvolumen</p>
              </div>
            </div>

            {(stats.wpUpdatesApprox > 0 || stats.themeUpdatesApprox > 0 || stats.pluginPushes > 0) && (
              <div className="grid grid-cols-2 gap-3">
                {(stats.wpUpdatesApprox > 0 || stats.themeUpdatesApprox > 0) && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">WP-Updates (ca.)</p>
                    <p className="text-xl font-bold text-gray-900">
                      {stats.wpUpdatesApprox + stats.themeUpdatesApprox}
                    </p>
                    <p className="text-xs text-yellow-700">
                      {stats.wpUpdatesApprox} Plugins, {stats.themeUpdatesApprox} Themes
                    </p>
                  </div>
                )}
                {stats.pluginPushes > 0 && (
                  <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Plugin-Rollouts</p>
                    <p className="text-xl font-bold text-gray-900">{stats.pluginPushes}</p>
                    <p className="text-xs text-sky-700">via BackupPilot</p>
                  </div>
                )}
              </div>
            )}

            {stats.backupsTotal === 0 && stats.pluginPushes === 0 && (
              <p className="text-sm text-gray-400 py-2">
                Keine Daten für {quarterLabel} vorhanden.
              </p>
            )}
          </div>
        ) : null}
      </div>

      {/* Senden */}
      <div className="flex items-center gap-3 pt-2 border-t">
        <button
          onClick={() => sendMutation.mutate()}
          disabled={!currentEmail || sendMutation.isPending}
          title={!currentEmail ? 'Bitte zuerst eine Kunden-E-Mail eintragen' : undefined}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sendMutation.isPending ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          {sendMutation.isPending
            ? 'Wird gesendet…'
            : `${quarterLabel} an ${currentEmail ?? 'Kunden-E-Mail'} senden`}
        </button>

        {sendResult && (
          <span className={`flex items-center gap-1.5 text-sm font-medium ${
            sendResult.success ? 'text-green-700' : 'text-red-700'
          }`}>
            {sendResult.success ? (
              <CheckCircle className="w-4 h-4" />
            ) : (
              <AlertCircle className="w-4 h-4" />
            )}
            {sendResult.message}
          </span>
        )}
      </div>

      {/* Historie */}
      {history && history.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Gesendete Berichte</h3>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Quartal</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Gesendet</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Backups</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {history.slice(0, 10).map(run => (
                  <tr key={run.id}>
                    <td className="px-4 py-2 font-medium text-gray-700">Q{run.quarter} {run.year}</td>
                    <td className="px-4 py-2 text-gray-500">
                      {formatDistanceToNow(new Date(run.sentAt), { addSuffix: true, locale: de })}
                    </td>
                    <td className="px-4 py-2 text-gray-500">{run.backupsCompleted}/{run.backupsTotal}</td>
                    <td className="px-4 py-2">
                      {run.success ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                          <CheckCircle className="w-3 h-3" /> Gesendet
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
                          <AlertCircle className="w-3 h-3" /> Fehler
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
